import React from "react";
import { matchPath, Route, Routes, useLocation } from "react-router-dom";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { PageSection } from "@design-system/PageSection/PageSection";
import { StateAwareNavigate } from "@components/routes/StateAwareNavigate";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { HEADER_HEIGHT } from "@constants";
import { styleWithVars } from "@lib/cssVars";
import { MainInterface } from "@components/mainInterface/MainInterface";
import { Breadcrumb } from "@components/mainInterface/MainInterfaceHeader";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { PlaygroundNavigation } from "./PlaygroundNavigation";
import {
    DESIGN_SYSTEM_PATH,
    designSystemEntryPath,
    playgroundComponents,
    PlaygroundComponentEntry,
} from "./playgroundComponents";
import styles from "./DesignSystemPage.module.scss";

const FillPlaygroundEntryPage = ({
    entry,
}: {
    entry: PlaygroundComponentEntry;
}) => (
    <div
        className={styles.fillEntry}
        style={styleWithVars({
            "--playground-header-height": `${HEADER_HEIGHT}px`,
        })}
    >
        <HStack gap={8} align="center" className={styles.fillEntryHeader}>
            <h2 className={styles.fillEntryTitle}>{entry.label}</h2>
            <p className={styles.entryDescription}>{entry.description}</p>
        </HStack>
        <div className={styles.fillEntryBody}>{entry.render()}</div>
    </div>
);

const PlaygroundEntryPage = ({ entry }: { entry: PlaygroundComponentEntry }) =>
    entry.layout === "fill" ? (
        <FillPlaygroundEntryPage entry={entry} />
    ) : (
        <>
            <PageSection className={styles.entryHeader}>
                <VStack gap={4}>
                    <h2 className={styles.entryTitle}>{entry.label}</h2>
                    <p className={styles.entryDescription}>
                        {entry.description}
                    </p>
                </VStack>
            </PageSection>
            {entry.render()}
        </>
    );

export const DesignSystemPage = () => {
    const { componentsPlayground: isComponentsPlaygroundEnabled } =
        useTypedFlags();
    const navigate = useStateAwareNavigate();
    const location = useLocation();

    if (!isComponentsPlaygroundEnabled) {
        return (
            <MainInterface sideNavigation={null}>
                <EmptyState
                    header="404"
                    body="Page not found"
                    ctaText="Go to home"
                    action={() => navigate("/")}
                />
            </MainInterface>
        );
    }

    const match = matchPath(
        `${DESIGN_SYSTEM_PATH}/:entryId`,
        location.pathname,
    );
    const activeEntry = playgroundComponents.find(
        (entry) => entry.id === match?.params.entryId,
    );

    const breadcrumbs: Breadcrumb[] = activeEntry
        ? [{ label: activeEntry.label }]
        : [];

    return (
        <MainInterface
            sideNavigation={<PlaygroundNavigation />}
            breadcrumbs={breadcrumbs}
        >
            <Routes>
                {playgroundComponents.map((entry) => (
                    <Route
                        key={entry.id}
                        path={entry.id}
                        element={<PlaygroundEntryPage entry={entry} />}
                    />
                ))}
                <Route
                    path="*"
                    element={
                        <StateAwareNavigate
                            to={designSystemEntryPath(
                                playgroundComponents[0].id,
                            )}
                            replace
                        />
                    }
                />
            </Routes>
        </MainInterface>
    );
};
